import { ArchiveIcon, EyeClosedIcon, Pencil1Icon } from "@radix-ui/react-icons";
import { AlertDialog, Button, Callout, Flex, Text } from "@radix-ui/themes";
import { useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetSubscriptionsByStudentQuery } from "../../hooks/useGetSubscriptionsByStudentQuery";
import { useGetTeacheIdByUserId } from "../../hooks/useGetTeacheIdByUserId";
import { setPdfPreviewType } from "../../redux/slices/report/report";
import { setSubscriptionData } from "../../redux/slices/school/subscription";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { getStudentDebtsBySubscription } from "../../services/finance/studentDebt";
import { getSubjectsSubscriptions } from "../../services/school/student/getSubscriptions";
import { updateSubscription } from "../../services/school/student/updateSubscription";
import { SubscriptionType } from "../../types/Student";
import { Key_subscriptions, PAGE_NUMBER, PAGE_SIZE } from "../../utils/constants";
import { getBankNameByEntity } from "./getBankNameByEntity";

const columnNamesMap: { [key: string]: string } = {
    student: 'Estudante',
    studentCode: 'Código',
    course: 'Curso',
    academicYear: 'Ano lectivo',
    semester: 'Semestre',
    ref: "Ref"
};
const columnSubjectsMap: { [key: string]: string } = {
    nr: 'Ord.',
    subject: 'Nome da Disciplina',
    level: 'Nivel',
    semester: 'Semestre',

};

export const StudentSubscription = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const [itemToBeDeleted, setItemToBeDeleted] = useState<SubscriptionType | null>(null);
    const { UserId } = useSelector((state: RootState) => state.login.userData)
    const { data: studentData } = useGetTeacheIdByUserId(UserId, { enabled: !!UserId });
    const { data, status } = useGetSubscriptionsByStudentQuery({ PageNumber: currentPage, PageSize: pageSize, SearchTerm: searchTerm, IdStudent: studentData?.idStudent },
        { enabled: !!studentData?.idStudent }

    );
    const mutationDelete = useMutation(updateSubscription, {
        onSuccess: () => {
            queryClient.invalidateQueries(Key_subscriptions);
            toast.success('Apagado');
        },
        onError: () => {
            toast.error("Erro ao apagar.")
        }
    });

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(PAGE_NUMBER);
    };

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        setCurrentPage(1);
    };

    const handleClickCreate = (event: any): void => {
        navigate(routes.createMySubscription)
    }
    const openDeleteDialog = (s: SubscriptionType) => {
        setItemToBeDeleted(s);
    };
    const openSeenDialog = async (subscription: SubscriptionType) => {
        try {

            const data = await getSubjectsSubscriptions({
                PageSize: 400,
                PageNumber: 1,
                IdSubscription: subscription.id,
            }
            );
            subscription.courseSubjects = data?.items as any
            subscription.planSubjects = data?.items as any

            // console.log(subscription);

            dispatch(setSubscriptionData(subscription));

        } catch (err: any) {
            toast.error(err?.message);
        }
        setItemToBeDeleted(subscription);
    };

    const confirmDelete = () => {
        if (itemToBeDeleted) {
            const updatedSubscription = { ...itemToBeDeleted, status: false };
            mutationDelete.mutateAsync(updatedSubscription);
            setItemToBeDeleted(null);
        }
    };
    const handlePrintSubscription = async (subscription: SubscriptionType) => {
        const data = await getSubjectsSubscriptions({
            PageSize: 400,
            PageNumber: 1,
            IdSubscription: subscription.id,
        }
        );
        subscription.courseSubjects = data?.items as any
        subscription.planSubjects = data?.items as any
        try {
            const studentDebtsResponse = await getStudentDebtsBySubscription(subscription.id!);
            const paymentsReferences = studentDebtsResponse
                .flatMap((sdr) => sdr.paymentReferences || [])
                .filter((ref) => ref?.id)
                .filter((ref) => ref.status);
            subscription.paymentsReferences = paymentsReferences;
            const paymentsTransactions = studentDebtsResponse
                .flatMap((sdr) => sdr.paymentTransactions || [])
                .filter((ref) => ref?.id)
                .filter((ref) => ref.status);
            subscription.paymentsTransactions = paymentsTransactions;
            const studentDebtsAssociations = studentDebtsResponse?.map(sdr => { return { idStudentDebt: sdr?.id, idSubscription: sdr?.idSubscription, idEnrollment: sdr?.idEnrollment, price: sdr?.originalAmount } })
            subscription.studentDebtsAssociations = studentDebtsAssociations;
            const idsSubjectsArray = Array.from(
                new Set(
                    studentDebtsResponse
                        ?.map((sdr) => JSON.parse(sdr?.idsSubjects || "[]"))
                        .flat()
                )
            );
            subscription.idsSubjectsArray = idsSubjectsArray;
            const studentDebtData = studentDebtsResponse?.[0];
            const paymentReferences = studentDebtData?.paymentReferences;
            const paymentTransactions = studentDebtData?.paymentTransactions;
            const paymentTransaction = paymentTransactions?.find(p => p?.status);
            const paymentReference = paymentReferences?.find(p => p?.status);
            subscription.course = subscription?.course;
            subscription.idsSubjects = studentDebtData?.idsSubjects;
            subscription.payment = {
                idEnrollment: studentDebtData?.idEnrollment,
                entity: paymentReference?.entity,
                reference: paymentReference?.referenceCode,
                totalPrice: paymentReference?.amount,
                paymentProof: paymentTransaction?.paymentProof,
                idStudentDebt: studentDebtData?.id,
                isPaymentByEntityReference: true,
                bank: await getBankNameByEntity(paymentReference?.entity!)
            }

        } catch (error) {
            console.log({ error })
        }
        dispatch(setSubscriptionData(subscription));
        dispatch(setPdfPreviewType('SUBSCRIPTION'));
        navigate(routes.pdfPreview);
    }
    const handleEdit = async (subscription: SubscriptionType) => {

        try {

            const data = await getSubjectsSubscriptions({
                PageSize: 400,
                PageNumber: 1,
                IdSubscription: subscription.id,
            }
            );
            subscription.courseSubjects = data?.items as any
            subscription.planSubjects = data?.items as any
            try {
                const studentDebtsResponse = await getStudentDebtsBySubscription(subscription.id!);
                const paymentsReferences = studentDebtsResponse
                    .flatMap((sdr) => sdr.paymentReferences || [])
                    .filter((ref) => ref?.id)
                    .filter((ref) => ref.status);
                subscription.paymentsReferences = paymentsReferences;
                const paymentsTransactions = studentDebtsResponse
                    .flatMap((sdr) => sdr.paymentTransactions || [])
                    .filter((ref) => ref?.id)
                    .filter((ref) => ref.status);
                subscription.paymentsTransactions = paymentsTransactions;
                const studentDebtsAssociations = studentDebtsResponse?.map(sdr => { return { idStudentDebt: sdr?.id, idSubscription: sdr?.idSubscription, idEnrollment: sdr?.idEnrollment, price: sdr?.originalAmount } })
                subscription.studentDebtsAssociations = studentDebtsAssociations;
                const idsSubjectsArray = Array.from(
                    new Set(
                        studentDebtsResponse
                            ?.map((sdr) => JSON.parse(sdr?.idsSubjects || "[]"))
                            .flat()
                    )
                );
                subscription.idsSubjectsArray = idsSubjectsArray;
                const studentDebtData = studentDebtsResponse?.[0];
                const paymentReferences = studentDebtData?.paymentReferences;
                const paymentTransactions = studentDebtData?.paymentTransactions;
                const paymentTransaction = paymentTransactions?.find(p => p?.status);
                const paymentReference = paymentReferences?.find(p => p?.status);
                subscription.course = subscription?.course;
                subscription.idsSubjects = studentDebtData?.idsSubjects;
                subscription.payment = {
                    idEnrollment: studentDebtData?.idEnrollment,
                    entity: paymentReference?.entity,
                    reference: paymentReference?.referenceCode,
                    totalPrice: paymentReference?.amount,
                    paymentProof: paymentTransaction?.paymentProof,
                    idStudentDebt: studentDebtData?.id,
                    isPaymentByEntityReference: true,
                    bank: await getBankNameByEntity(paymentReference?.entity!)
                }

            } catch (error) {
                console.log({ error })
            }
            dispatch(setSubscriptionData(subscription));
            /*  dispatch(setStudentData(studentData));
              dispatch(
                setUserData({...userData,
                            isForeign: stringToBoolean(studentData.isForeign)
                        } as any
            )); */
            navigate(routes.updateSubscription);
        } catch (err: any) {
            toast.error(err?.message);
        }


    }

    const options = data?.items.map(() => [
        // {
        //     label: 'Editar',
        //     onClick: (s: SubscriptionType) => handleEdit(s),
        //     icon: <Pencil1Icon />
        // },
        {
            label: 'Ver disciplinas',
            onClick: (s: SubscriptionType) => openSeenDialog(s),
            icon: <EyeClosedIcon />
        },
        {
            label: 'Imprimir',
            onClick: (s: SubscriptionType) => handlePrintSubscription(s),
            icon: <ArchiveIcon />
        },

    ]) || [];

    if (!studentData || !studentData.idStudent) {
        return (
            <Flex direction="column">
                <Callout.Root color="red" role="alert">
                    <Text>
                        Entre como estudante &nbsp;
                        <Link to={routes.dashboard} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
                    </Text>
                </Callout.Root>

            </Flex>
        );
    }

    return (
        <>
            <Lists
                status={status}
                TableComponent={() =>
                    <TableComponent
                        columns={Object.keys(columnNamesMap)}
                        items={
                            data?.items.map((s => { return { ...s, ref: `${s.academicYear}/${s.semester}` } }))}
                        options={options}
                        columnNamesMap={columnNamesMap}
                        totalPages={data?.totalPages || 1}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange}
                        onSearch={handleSearch}
                        search={searchTerm}
                        totalItems={data?.totalItems || 0}
                    />
                }
                handleClickCreate={handleClickCreate}
            />
            {itemToBeDeleted && (
                <AlertDialog.Root open={!!itemToBeDeleted} onOpenChange={() => setItemToBeDeleted(null)} >
                    <AlertDialog.Content maxWidth="1000px" >
                        <AlertDialog.Title>Disciplinas associadas </AlertDialog.Title>
                        <Flex gap="3" mt="4" justify="center">
                            <Lists
                                status={status}
                                TableComponent={() =>
                                    <TableComponent
                                        columns={Object.keys(columnSubjectsMap)}
                                        items={
                                            itemToBeDeleted.courseSubjects?.map((k, index) => {
                                                return { ...k, nr: index + 1 }

                                            })}

                                        columnNamesMap={columnSubjectsMap}
                                        totalPages={data?.totalPages || 1}
                                        currentPage={currentPage}
                                        onPageChange={handlePageChange}
                                        // pageSize={pageSize}
                                        // onPageSizeChange={handlePageSizeChange} 
                                        enableSearchByBtn={false}
                                        enableSearchByEnter={false}
                                        enableSearch={false}
                                        enablePaginate={false}



                                    />
                                }
                            />

                        </Flex>
                        <Flex gap="3" justify="end" mt="4">
                            <AlertDialog.Cancel>
                                <Button variant="soft" color="gray" >
                                    Cancelar
                                </Button>
                            </AlertDialog.Cancel>

                        </Flex>
                    </AlertDialog.Content>
                </AlertDialog.Root>
            )}
        </>
    );
}